import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from '../../../../../utils/styles';
import Link from '../../../../../components/Common/UI/Link';
import { generateMenuItems } from '../../../../../infoTrac/components/Layout/Menu';
import { guidanceRoutes } from '../index';
import { Button } from '../../../../../components/Common/UI/Button';
import MenuIcon from '../MenuIcon';
import { getAccountFromStorage, useAccountContextProvider } from '../../../../context/account';

export type MainNavigationProps = React.HTMLAttributes<HTMLElement>;

const cleanPath = (path: string) => path.replace(/^\/|\/$/g, '');

export default function MainNav({ className, ...props }: MainNavigationProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);

  let account = getAccountFromStorage();

  const menuItems = useMemo(
    () => generateMenuItems(guidanceRoutes).filter(item => (!account?.number ? !item?.visibleOnlyWithAccount : true)),
    [account],
  );

  const { account: contentAccount } = useAccountContextProvider();

  useEffect(() => {
    account = getAccountFromStorage();
  }, [contentAccount]);

  useEffect(() => {
    const iframePath = sessionStorage.getItem('iframe-path');

    /** If iframe path set to empty string, then dont touch it. '' triggers notices modal */
    if (iframePath !== '') {
      sessionStorage.setItem('iframe-path', pathname);
    }
  }, [pathname]);

  return (
    <nav ref={menuRef} className={cn('grid gap-y-[20px]', className)} {...props}>
      {menuItems.map(({ path, label, icon, children }) => (
        <div key={path} className="group">
          {children?.length ? (
            <Button
              variant="ghost"
              className="!bg-white flex gap-[10px] items-center p-0 text-[18px] text-addition1-80
              hover:text-addition1"
              onClick={() => {
                navigate('/guidance/statements/checking-statements');
              }}
            >
              <MenuIcon
                icon={icon}
                active={pathname.includes(path)}
                className="group-hover:[&_svg_path]:fill-white group-hover:bg-secondary group-hover:border-secondary"
              />
              {label}
            </Button>
          ) : (
            <Link
              to={path}
              variant="guidance"
              active={cleanPath(pathname) === cleanPath(path)}
              className="group-hover:[&_svg_path]:fill-white group-hover:[&_div]:bg-secondary
              group-hover:[&_div]:border-secondary"
              icon={icon}
            >
              {label}
            </Link>
          )}
          {pathname.includes(path)
            ? children?.map(el => (
                <Link
                  to={el.path}
                  key={el.path}
                  className={cn('text-[16px] mt-[15px] h-[30px] font-normal text-addition1', {
                    'font-bold': pathname.endsWith(el.path),
                  })}
                >
                  <div className="bg-addition1 rounded w-[5px] h-[5px] mx-[5px]" />
                  {el.label}
                </Link>
              ))
            : null}
        </div>
      ))}
    </nav>
  );
}
